import { api } from '../api/client';

import { API_CHARTS_RUN_URL, API_CHARTS_URL } from '../utils';
import { retryAfterMs } from '../utils/retryAfter';
import { withElementChoicesField } from '../utils/elementRefusal';

/**
 * Saved charts (keepr-api docs/SCHEMA.md "Charts" › "Saved charts", "The
 * batch run", "The preview"; plan docs/plans/analytics.md §4.7, §5.8).
 *
 * A chart lives in one collection, in one of the two tiers every stored thing
 * has: `scope: 'collection'` (pinned for everyone, a manager's) or
 * `scope: 'user'` (the caller's own). A sub-collection inherits no chart from
 * the collections above it (owner ruling 2026-10-21, KPR-1084): a chart is
 * listed, opened and run only in its own collection. The server answers `canEdit` on every
 * chart — whether THIS caller may change it here — so the client never
 * re-derives the tier rules.
 *
 * "Gone", "not yours" and "unreadable" are one answer everywhere: someone
 * else's personal chart is the same 404 as a made-up id, and the same
 * `chart_unavailable` slot in a run. Global consumers and link-only readers
 * get no charts at all (ruled d14).
 *
 * Chart wire shape:
 *   { _id, collection_id, scope, userId, name, description, spec, canEdit,
 *     createdBy, createdAt, updatedBy, updatedAt }
 */

export const CHART_SCOPE = Object.freeze({ COLLECTION: 'collection', USER: 'user' });

/** The most runs one batch request may carry (⚑ d12; 400 `too_many_runs` past it). */
export const MAX_RUNS = 24;

const chartUrl = (collectionId, chartId) => `${API_CHARTS_URL(collectionId)}/${chartId}`;

/**
 * The collection's own charts — the collection tier first, then the caller's
 * own, by name within each. The API's `inherited` is always empty since
 * KPR-1084 and is not read.
 */
export async function getChartLists(collectionId) {
	const { data } = await api.get(API_CHARTS_URL(collectionId));
	return { charts: Array.isArray(data?.charts) ? data.charts : [] };
}

/** One chart: this collection's own (404 for anything else). */
export async function getChart(collectionId, chartId) {
	const { data } = await api.get(chartUrl(collectionId, chartId));
	return data;
}

/**
 * Always creates a PERSONAL chart (pinning it for everyone is promote's
 * job). Answers the chart plus the spec's `warnings` (what the chart will
 * say about itself). `description` is sent only when there is one.
 */
export async function createChart(collectionId, { name, description, spec, elementChoices = null }) {
	const payload = { name, spec };
	if (typeof description === 'string' && description.trim()) payload.description = description;
	// The person's answer to an ambiguous element name in the filter (utils/elementRefusal.js).
	const { data } = await api.post(API_CHARTS_URL(collectionId), withElementChoicesField(payload, elementChoices));
	return data;
}

/**
 * A partial update: only the keys given are sent. `description: null` or
 * `''` clears it. The tier is not patchable (promote and demote are their
 * own endpoints), and an inherited chart is changed where it is defined.
 */
export async function updateChart(collectionId, chartId, patch = {}) {
	const payload = {};
	if (patch.name !== undefined) payload.name = patch.name;
	if (patch.description !== undefined) payload.description = patch.description === '' ? null : patch.description;
	if (patch.spec !== undefined) payload.spec = patch.spec;
	const { data } = await api.patch(chartUrl(collectionId, chartId), withElementChoicesField(payload, patch.spec !== undefined ? patch.elementChoices : null));
	return data;
}

/** Hard delete, restorable from History. */
export async function deleteChart(collectionId, chartId) {
	await api.delete(chartUrl(collectionId, chartId));
}

/** manage — "Pin for everyone": the chart moves to the collection tier. */
export async function promoteChart(collectionId, chartId) {
	const { data } = await api.post(`${chartUrl(collectionId, chartId)}/promote`);
	return data;
}

/** manage — "Unpin — return to your own list": the chart becomes the CALLER's personal chart. */
export async function demoteChart(collectionId, chartId) {
	const { data } = await api.post(`${chartUrl(collectionId, chartId)}/demote`);
	return data;
}

/** Runs an unsaved spec (the builder's preview): { spec, warnings, result }. */
export async function previewChart(collectionId, spec, { signal, elementChoices = null } = {}) {
	const { data } = await api.post(`${API_CHARTS_URL(collectionId)}/preview`, withElementChoicesField({ spec }, elementChoices), signal ? { signal } : undefined);
	return data;
}

/**
 * The batch run: up to MAX_RUNS saved charts in ONE request — one request per
 * surface, never one per chart. Each run names its CONTEXT, the collection
 * whose items it counts (the chart's own, or a sub-collection that inherits
 * it), and may replace the chart's own date range for this run: `range` is
 * the spec's `time.range` shape (`{ preset }` or `{ from, to }`), or `null`
 * for all time; left out, the chart's own range applies. A dashboard's run
 * may also carry its `filter` (charts A6): the viewer's own question, ANDed
 * on the chart's own filter by the server; left out or empty, none is sent.
 * `grade: 'export'` runs it at the export grade (A9: what an item export
 * would hand the caller, no doors — runChartForExport); left out, the
 * screen's.
 *
 * Answers { ranAt, results }: one slot per run, in order — a run result
 * (with `applied: { range?, filter? }` for what was sent), or
 * `{ ok: false, chart_id, error: { code, message, path?, needs?, cause?, retryAfter?, budget? } }`
 * — `budget` (`items`, `personItems`, `formulas`) on a slot a work budget
 * refused, which the frame's sentence is keyed on whichever way it came.
 */
export async function runCharts(runs, { signal } = {}) {
	const list = (Array.isArray(runs) ? runs : []).map((run) => {
		const out = { chart_id: run.chart_id, collection_id: run.collection_id };
		if (run.range !== undefined) out.range = run.range;
		if (typeof run.filter === 'string' && run.filter.trim()) out.filter = run.filter;
		if (run.grade === 'export') out.grade = 'export';
		return out;
	});
	if (!list.length || list.length > MAX_RUNS) throw new Error(`A batch runs 1 to ${MAX_RUNS} charts.`);
	const { data } = await api.post(API_CHARTS_RUN_URL, { runs: list }, signal ? { signal } : undefined);
	return {
		ranAt: data?.ranAt || null,
		results: Array.isArray(data?.results) ? data.results : [],
	};
}

const retryAfterOf = (error) => {
	const ms = retryAfterMs(error, { body: true });
	return ms === null ? null : ms / 1000;
};

/**
 * A refused batch REQUEST as the failed slot of each run it carried, so a
 * chart's frame says what happened in its own words (charts/chartNotes.js
 * errorSentence): the charts class's 429 (`rate_limited`, with its wait), the
 * process-wide limit's 503 (`charts_busy`), a refused body (a range's own
 * refusal, `not_offered_yet`), or no answer at all (`chart_failed`).
 */
export function failedSlot(error, chartId = null) {
	const status = error?.response?.status;
	const body = error?.response?.data || {};
	const slot = (code, message, extra = {}) => ({ ok: false, chart_id: chartId, error: { code, message, ...extra } });
	if (status === 429) {
		const retryAfter = retryAfterOf(error);
		const extra = retryAfter === null ? {} : { retryAfter };
		// Which budget refused it: `inFlight` is the person's own chart request
		// still running (A3's per-person share), which waiting answers;
		// `formulas` the person's formula-work budget, which has its own
		// sentence (chartNotes errorSentence) — as a batch slot's `budget` has.
		if (typeof body.budget === 'string') extra.budget = body.budget;
		return slot('rate_limited', body.message || '', extra);
	}
	if (status === 503) {
		const retryAfter = retryAfterOf(error);
		return slot(body.code || 'charts_busy', body.message || '', retryAfter === null ? {} : { retryAfter });
	}
	if (status && typeof body.code === 'string') {
		const extra = {};
		if (typeof body.path === 'string') extra.path = body.path;
		if (typeof body.needs === 'string') extra.needs = body.needs;
		// The stored query's own code behind a refused filter (`invalid_filter`)
		// — and, for an element the filter could mean several of (element ids
		// step 3), what the person answers it with.
		if (typeof body.cause === 'string') extra.cause = body.cause;
		['element', 'choiceKey', 'candidates', 'control'].forEach((key) => { if (body[key] !== undefined) extra[key] = body[key]; });
		return slot(body.code, typeof body.message === 'string' ? body.message : '', extra);
	}
	return slot('chart_failed', '');
}

// The waits a refusal names, in seconds, when it names none (useChartRun's).
const EXPORT_RETRY_WAIT_S = { inFlight: 2, charts_busy: 5 };
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * DOWNLOAD DATA's run (A9, ⚑ d25): one saved chart at the EXPORT grade —
 * what an item export would hand the caller, never the screen's answer — in
 * its context, with the range the page shows (`range` as runCharts takes it).
 * Answers the run's slot: a result (`grade: 'export'`, no doors) or a failed
 * slot. The person may have one chart request in flight and one waiting (A3),
 * so a download pressed while the page's own run is out may be told to wait
 * (`inFlight`, or `charts_busy` from a busy server): it waits the time named
 * and asks once more.
 */
export async function runChartForExport({ chartId, collectionId, range }, { signal } = {}) {
	const request = { chart_id: chartId, collection_id: collectionId, grade: 'export' };
	if (range !== undefined) request.range = range;
	const ask = async () => {
		try {
			const answer = await runCharts([request], { signal });
			return answer.results[0] || failedSlot(null, chartId);
		} catch (e) {
			return failedSlot(e, chartId);
		}
	};
	const first = await ask();
	const error = first && first.ok === false ? first.error || {} : null;
	const kind = error && (error.code === 'charts_busy' ? 'charts_busy' : (error.code === 'rate_limited' && error.budget === 'inFlight' ? 'inFlight' : null));
	if (!kind) return first;
	const named = typeof error.retryAfter === 'number' && Number.isFinite(error.retryAfter) ? error.retryAfter : EXPORT_RETRY_WAIT_S[kind];
	await sleep(Math.max(1, Math.ceil(named)) * 1000);
	return ask();
}

/** The sentence for a refused chart WRITE (rename, delete, pin): the server's own words where it gave them. */
export function chartRefusal(error, fallback) {
	const status = error?.response?.status;
	const body = error?.response?.data || {};
	if (status === 404) return 'This chart is no longer available.';
	if (typeof body.message === 'string' && body.message.trim() && status && status < 500) return body.message.trim();
	if (status === 503) return 'keepr is busy right now. Try again in a few seconds.';
	return fallback;
}
